
import { useRef, useEffect, useState, RefObject } from 'react';

interface UseIntersectionObserverProps {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  onIntersect?: () => void;
  enabled?: boolean;
}

/**
 * Custom hook to handle intersection observer
 * Used for infinite scrolling and lazy loading
 */
export function useIntersectionObserver<T extends HTMLElement = HTMLDivElement>({
  root = null,
  rootMargin = '0px',
  threshold = 0.1,
  onIntersect,
  enabled = true
}: UseIntersectionObserverProps): [RefObject<T>, boolean] {
  const targetRef = useRef<T>(null);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !targetRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsIntersecting(entry.isIntersecting);
        
        if (entry.isIntersecting && onIntersect) {
          onIntersect();
        }
      },
      { root, rootMargin, threshold }
    );
    
    observer.observe(targetRef.current);
    
    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [enabled, root, rootMargin, threshold, onIntersect]);

  return [targetRef, isIntersecting];
}
